/**
 * Turning the API's error envelope into something a UI can branch on.
 *
 * The server answers failures with:
 *
 * ```json
 * { "status": { "code": 401, "type": "invalid_credentials",
 *               "message": "…", "errors": [...], "context": {...} } }
 * ```
 *
 * `type` is the machine-readable discriminator — the same vocabulary the
 * Flutter client switches on — and `message` is free text meant for a human.
 * **Branch on `type`, never on `message`.** Messages are translated and
 * reworded; types are contract.
 */

/** Field-level validation failures, in the shape the server sends. */
export interface FieldError {
  property: string;
  messages: string[];
}

/** The shape of a failed response body. */
export interface ErrorEnvelope {
  status: {
    code: number;
    type: string;
    message: string;
    errors?: FieldError[];
    context?: Record<string, unknown>;
  };
}

/**
 * An error raised by the API.
 *
 * Carries the whole envelope rather than flattening it: `context` is how the
 * server hands a client what it needs to continue a flow — a `challenge_id` on
 * an MFA step-up, a `retry_after` on a rate limit — and discarding it here
 * would mean re-fetching what the failure already told us.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly type: string;
  readonly fieldErrors: FieldError[];
  readonly context: Record<string, unknown>;

  constructor(envelope: ErrorEnvelope) {
    super(envelope.status.message);
    this.name = "ApiError";
    this.status = envelope.status.code;
    this.type = envelope.status.type;
    this.fieldErrors = envelope.status.errors ?? [];
    this.context = envelope.status.context ?? {};
  }

  /**
   * Field errors keyed by property, for handing to a form library.
   *
   * A field can fail several rules at once, so the messages stay an array —
   * showing only the first would make a user fix one problem at a time.
   */
  get byField(): Record<string, string[]> {
    return Object.fromEntries(this.fieldErrors.map((e) => [e.property, e.messages]));
  }

  /** Whether the caller must sign in again. */
  get isAuthenticationFailure(): boolean {
    return this.status === 401;
  }

  /** Whether the caller is signed in but not permitted. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Seconds to wait, from a rate-limit response.
   *
   * Read from `context`, which the server populates alongside the
   * `Retry-After` header. Null when the failure was not a rate limit.
   */
  get retryAfterSeconds(): number | null {
    const value = this.context.retry_after;
    return typeof value === "number" ? value : null;
  }
}

/**
 * A request that never reached the API — offline, DNS, TLS, a cancelled fetch.
 *
 * Kept distinct from {@link ApiError} because the two need opposite handling:
 * a network failure is worth retrying and worth telling the user is probably
 * their connection, while a 4xx is neither.
 */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("The request could not be completed. Check your connection.");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

/** Whether a value is an {@link ApiError} with one of the given types. */
export function isApiErrorOfType(error: unknown, ...types: string[]): boolean {
  return error instanceof ApiError && types.includes(error.type);
}

/** Parse a failed response body, falling back if it is not an envelope. */
export async function toApiError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ErrorEnvelope;
    if (body?.status?.type) return new ApiError(body);
  } catch {
    // A non-JSON body — a proxy's HTML error page, a truncated response. Fall
    // through to a synthetic envelope rather than throwing while handling an
    // error, which would replace a useful failure with a confusing one.
  }
  return new ApiError({
    status: {
      code: response.status,
      type: response.status >= 500 ? "service_unavailable" : "unknown_error",
      message: `The server responded with ${String(response.status)}.`,
    },
  });
}
